import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { filter, catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environment/environment';
import { User } from '../user/user.types';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { OAuthService } from 'angular-oauth2-oidc';

export interface UrlItem {
  menu_name: string;
  menu_url: string;
  user_id: number;
  menu_id: number;
}

// Interface to match the expected menuurllist type
export interface MenuUrlList {
  menu_name: string;
  menu_url: string;
  user_id: number;
  menu_id: number;
  forEach?: any; // Add forEach property to match menuurllist type
}

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    private _checkUrl: boolean = false;
    private hasAccess: boolean = false;
    
    private _IsLogin: boolean = false;
    private apiUrl = environment.baseUrl;
    private apiUserId = environment.createdby;
    public NoRestrictions = false;
    user: User;
    peUserId: any;
    private _jwtHelper = new JwtHelperService();

    // This will hold the user data fetched from the user-mapping API
    private _userFromToken: any = null;
    
    // Dynamic URL list - will be populated from API only
    private userMenuUrlList: UrlItem[] = [];
    private _userMenuUrlsLoaded = false; // Add flag to track if URLs are loaded

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _router: Router,
        private oauthService: OAuthService,
    ) {
         this.oauthService.events
            .pipe(filter((e) => e.type === 'token_received'))
            .subscribe(() => {
                this.oauthService.setupAutomaticSilentRefresh();
            });
    }

    /**
     * This is the main entry point for SSO authentication, called by the AuthGuard.
     * It handles the entire sequence: token validation, user mapping, and permission loading.
     */
     public runInitialLoginSequence(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.oauthService.loadDiscoveryDocumentAndTryLogin().then(isLoggedIn => {
                if (isLoggedIn && this.oauthService.hasValidAccessToken()) {
                    // If login is successful, start the user mapping and permission loading sequence.
                    this._performSsoUserInitialization().subscribe({
                        next: (success) => {
                            if (success) {
                                this.oauthService.setupAutomaticSilentRefresh();
                                this.setAuthenticated(true);
                                console.log('Successfully logged in and initialized SSO user.');
                                resolve(true);
                            } else {
                                console.error('SSO user initialization failed.');
                                this.signOut();
                                reject(new Error('Could not initialize SSO user.'));
                            }
                        },
                        error: (err) => {
                            console.error('Error during SSO user initialization:', err);
                            this.signOut();
                            reject(err);
                        }
                    });
                } else {
                    // If not logged in, redirect to the identity provider.
                    console.log('Not logged in, initiating code flow.');
                    this.oauthService.initCodeFlow();
                    resolve(false);
                }
            }).catch((error) => {
                console.error('Error during initial login sequence, initiating code flow.', error);
                this.oauthService.initCodeFlow();
                reject(error);
            });
        });
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ SSO and User Mapping Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetches the application-specific user data using the peId from the SSO token.
     * @param peId The 'sub' claim from the SSO token.
     */
    private getUserMapping(peId: string): Observable<any> {
        return this._httpClient.get<any>(`${this.apiUrl}UserLogin/user-mapping/${peId}`);
    }

    /**
     * Orchestrates the entire user initialization flow for an SSO user.
     * 1. Extracts peId from token.
     * 2. Calls user-mapping API.
     * 3. Calls menu URL list API with the mapped userId.
     * 4. Stores the final user object.
     */
    private _performSsoUserInitialization(): Observable<boolean> {
        const claims = this.oauthService.getIdentityClaims();
        if (!claims || !claims['sub']) {
            return throwError(() => new Error('PE ID (sub) not found in token claims.'));
        }
        const peId = claims['sub'];

        return this.getUserMapping(peId).pipe(
            switchMap((mappingResponse: any) => {
                if (mappingResponse && mappingResponse.strResult === 'Success') {
                    // Create the initial user object from the mapping response
                    this._createAndStoreUserFromMapping(mappingResponse);
                    // Fetch menu URLs using the dynamic user ID
                    return this.loadUserMenuUrls(mappingResponse.intUserId);
                }
                return throwError(() => new Error(mappingResponse.strMessage || 'User mapping failed.'));
            }),
            map(urlList => {
                // Finalize the user object by adding the fetched URLs
                this._updateUserUrlsAndFinalize(urlList);
                return true;
            }),
            catchError(error => {
                console.error('Error in SSO initialization pipeline:', error);
                return of(false);
            })
        );
    }

    /**
     * Helper to create the user object from the mapping API response.
     */
    private _createAndStoreUserFromMapping(mappingResponse: any): void {
        this._userFromToken = {
            id: mappingResponse.intUserId,
            email: mappingResponse.strUsername,
            userName: mappingResponse.strUsername,
            avatar: './assets/images/avatars/brian-hughes.jpg', // This seems static
            partner_id: mappingResponse.partner_id,
            externaluser_indicator: mappingResponse.externaluser_indicator,
            company_id: mappingResponse.company_id,
            partnerName: mappingResponse.partnerName,
            profile_image_name: mappingResponse.profile_image_name,
            profile_image_patner: mappingResponse.profile_image_patner,
            urllist: [], // To be populated later
            role_id: mappingResponse.role_id,
            user_PartnerName: mappingResponse.user_PartnerName
        };
        this.apiUserId = mappingResponse.intUserId;
    }

    /**
     * Helper to add menu URLs to the user object and update the user service.
     */
    private _updateUserUrlsAndFinalize(urlList: UrlItem[]): void {
        if (this._userFromToken) {
            this._userFromToken.urllist = this.convertToMenuUrlList(urlList);
            this._userService.user = this._userFromToken;
            this.accessUser = JSON.stringify(this._userFromToken); // Persist user to localStorage
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Dynamic URL List Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetch user menu URL list from API
     */
    getUserMenuUrlList(userId: number): Observable<UrlItem[]> {
        return this._httpClient.get<UrlItem[]>(`${this.apiUrl}UserLogin/GetUserMenuUrlList/${userId}`)
            .pipe(
                tap(urlList => {
                    this.userMenuUrlList = urlList;
                    this._userMenuUrlsLoaded = true;
                    console.log('User menu URL list loaded:', urlList);
                }),
                catchError(error => {
                    console.error('Error loading user menu URL list:', error);
                    this.userMenuUrlList = [];
                    this._userMenuUrlsLoaded = false;
                    return throwError(error);
                })
            );
    }

    /**
     * Get current user's URL list (dynamic only)
     */
    getCurrentUserUrlList(): UrlItem[] {
        return this.userMenuUrlList;
    }

    /**
     * Check if user menu URLs are loaded
     */
    areUserMenuUrlsLoaded(): boolean {
        return this._userMenuUrlsLoaded;
    }

    /**
     * Load user menu URLs after authentication
     */
    private loadUserMenuUrls(userId: number): Observable<UrlItem[]> {
        return this.getUserMenuUrlList(userId);
    }

    /**
     * Convert UrlItem[] to the format expected by User type
     */
    private convertToMenuUrlList(urlItems: UrlItem[]): any[] {
        return urlItems.map(item => ({
            ...item,
            forEach: Array.prototype.forEach // Add forEach method if needed
        }));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        debugger
        const oauthToken = this.oauthService.getAccessToken();
        if (oauthToken) {
            debugger
            return oauthToken;
        }
        return localStorage.getItem('accessToken') ?? '';
    }

    set accessUser(user: any) {
        debugger
        if (typeof user === 'string') {
            localStorage.setItem('user', user);
        } else {
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    get accessUserObject(): any {
        try {
            // Prioritize in-memory user object from the SSO flow
            if (this._userFromToken) {
                this._userFromToken.urllist = this.convertToMenuUrlList(this.getCurrentUserUrlList());
                return this._userFromToken;
            }

            // Fallback to localStorage for non-SSO or subsequent page loads
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                // Cache it in memory for this session
                this._userFromToken = parsedUser;
                return parsedUser;
            }

            return null;
        } catch (error) {
            console.error('Error getting user data:', error);
            return null;
        }
    }

    get accessUser(): string {
        try {
            const userObject = this.accessUserObject;
            return userObject ? JSON.stringify(userObject) : '{}';
        } catch (error) {
            console.error('Error serializing user data:', error);
            return '{}';
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set authentication status
     */
    setAuthenticated(status: boolean): void {
        this._authenticated = status;
    }

    /**
     * Check the authentication status, especially on page reloads.
     */
    check(): Observable<boolean> {
        // If already authenticated in the current session, no need to re-check.
        if (this._authenticated) {
            return of(true);
        }

        // For SSO users on page refresh, the token is still valid but user data is not in memory.
        if (this.oauthService.hasValidAccessToken()) {
            // If user data is not loaded, re-run the initialization sequence.
            if (!this._userFromToken || !this._userMenuUrlsLoaded) {
                return this._performSsoUserInitialization().pipe(
                    tap(success => {
                        if (success) {
                            this._authenticated = true;
                        }
                    })
                );
            }
            // If data is already loaded, just set authenticated flag.
            this._authenticated = true;
            return of(true);
        }

        // Fallback to original non-SSO token check
        if (!this.accessToken) {
            return of(false);
        }

        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        return this.signInUsingToken();
    }

    isAuthenticatedUser(): boolean {
        return this._authenticated || this.oauthService.hasValidAccessToken();
    }

    initPeUserId(): number {
        
       debugger
        const decodedToken = this._jwtHelper.decodeToken(this.accessToken);
        const userId = decodedToken?.pe_user_id;
        this.peUserId = +userId;
        return userId;
    }

    /**
     * Sign out
     */
    signOut() {
        this.oauthService.logOut();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        this._authenticated = false;
        this._userFromToken = null;
        this.userMenuUrlList = [];
        this._userMenuUrlsLoaded = false;
    }

    /**
     * URL Authorization Check
     */
    isCheckURL(): boolean {
        let currentURLValue = window.location.pathname;
        const mainURLValue = currentURLValue.split('/')[1];

        if (mainURLValue === "auto-login") {
            currentURLValue = '/quotation/add';
        }

        if (currentURLValue === "/change-password" || currentURLValue === "/") {
            return true;
        }

        if (currentURLValue === "/sign-in") {
            currentURLValue = '/dashboards/customer';
        }

        const userData = this.accessUserObject;
        if (!userData) {
            console.error('No user data available for URL check');
            return false;
        }

        // For SSO users, if menu URLs are still loading, deny access temporarily.
        // The guard should prevent this state from being reached in normal flow.
        if (this.oauthService.hasValidAccessToken() && !this._userMenuUrlsLoaded) {
            console.warn('User menu URLs not yet loaded for SSO user. Deferring URL check.');
            return false;
        }

        const urlList = userData.urllist || [];
        
        if (!Array.isArray(urlList) || urlList.length === 0) {
            console.warn('No URL list found for user');
            this.NoRestrictions = true; // This seems like a potential security issue.
            return false;
        }

        let hasAccess = urlList.some((menuurl: any) => {
            const menuMainURL = menuurl.menu_url?.split('/')[1];
            return menuMainURL === mainURLValue;
        });

        // Special case for the /norights/norights URL
        if (currentURLValue === "/norights/norights") {
            hasAccess = true;
        }

        if (!hasAccess) {
            console.warn(`Access denied for URL: ${currentURLValue}`);
        }

        return hasAccess;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Legacy methods (no changes needed for SSO implementation)
    // -----------------------------------------------------------------------------------------------------

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(
            this.apiUrl + 'Forgotpassword/PostForgotPassword/',
            email
        );
    }

    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    signIn(credentials: { Email: string; Password: string; IpAddress: string }, ipId): Observable<any> {
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        credentials.IpAddress = ipId;

        return this._httpClient
            .post(this.apiUrl + 'UserLogin/Login', credentials)
            .pipe(
                switchMap((response: any) => {
                    this.accessToken = response.strToken;

                    if (response.strResult === 'Success') {
                        this._authenticated = true;
                        this._IsLogin = true;
                        
                        return this.loadUserMenuUrls(response.intUserId).pipe(
                            switchMap(urlList => {
                                const res = {
                                    id: response.intUserId,
                                    email: response.strUsername,
                                    userName: response.strUsername,
                                    avatar: './assets/images/avatars/brian-hughes.jpg',
                                    partner_id: response.partner_id,
                                    externaluser_indicator: response.externaluser_indicator,
                                    company_id: response.company_id,
                                    partnerName: response.partnerName,
                                    profile_image_name: response.profile_image_name,
                                    profile_image_patner: response.profile_image_patner,
                                    urllist: this.convertToMenuUrlList(urlList),
                                    role_id: response.role_id,
                                    user_PartnerName: response.user_PartnerName,
                                };

                                this.accessUser = JSON.stringify(res);
                                this._userService.user = res;
                                this.apiUserId = res.id;
                                
                                return of(response);
                            }),
                            catchError(error => {
                                console.error('Failed to load user menu URLs:', error);
                                return throwError('Authentication failed: Unable to load user permissions');
                            })
                        );
                    }

                    return of(response);
                })
            );
    }

    public getIPAddress() {
        return this._httpClient.get('https://api.ipify.org?format=json');
    }

    public getUserLocation(): Observable<string> {
        const cached = localStorage.getItem('userLocation');
        if (cached !== "Unknown" && cached) {
            return of(cached); 
        }

        return this._httpClient.get<any>("https://ipapi.co/json/").pipe(
            map(data => `${data.city}, ${data.region}, ${data.country_name}`),
            tap(fullLocation => {
                localStorage.setItem('userLocation', fullLocation);
            })
        );
    }

    signInUsingToken(): Observable<any> {
        return this._httpClient
            .post('api/auth/sign-in-with-token', {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() => of(false)),
                switchMap((response: any) => {
                    if (response.accessToken) {
                        this.accessToken = response.accessToken;
                    }

                    this._authenticated = true;
                    this._userService.user = response.user;
                    return of(true);
                })
            );
    }

    signUp(user: { name: string; email: string; password: string; company: string; }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    unlockSession(credentials: { email: string; password: string; }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    Autologin(urlid: String): Observable<any> {
        return this._httpClient
            .get(this.apiUrl + 'UserLogin/AutoLogin/' + urlid)
            .pipe(
                switchMap((response: any) => {
                    this.accessToken = response.strToken;

                    if (response.strResult === 'Success') {
                        this._authenticated = true;
                        this._IsLogin = true;
                        
                        return this.loadUserMenuUrls(response.intUserId).pipe(
                            switchMap(urlList => {
                                const res = {
                                    id: response.intUserId,
                                    email: response.strUsername,
                                    userName: response.strUsername,
                                    avatar: './assets/images/avatars/brian-hughes.jpg',
                                    partner_id: response.partner_id,
                                    externaluser_indicator: response.externaluser_indicator,
                                    company_id: response.company_id,
                                    partnerName: response.partnerName,
                                    profile_image_name: response.profile_image_name,
                                    profile_image_patner: response.profile_image_patner,
                                    urllist: this.convertToMenuUrlList(urlList),
                                    role_id: response.role_id,
                                };

                                this.accessUser = JSON.stringify(res);
                                this._userService.user = res;
                                this.apiUserId = res.id;
                                
                                return of(response);
                            }),
                            catchError(error => {
                                console.error('Failed to load user menu URLs:', error);
                                return throwError('Authentication failed: Unable to load user permissions');
                            })
                        );
                    }

                    return of(response);
                })
            );
    }
}
